import {
  type ProviderKind,
  type ProviderModelOptions,
  type ScopedThreadRef,
  type ServerProviderModel,
} from "@cmmd-forge/contracts";
import type { ReactNode } from "react";
import type { DraftId } from "../../draftId";
import { ReasoningEffortPicker } from "./ReasoningEffortPicker";
import { TraitsMenuContent } from "./TraitsPicker";
import {
  getComposerProviderState,
  type ComposerProviderState,
  type ComposerProviderStateInput,
} from "./composerProviderState";

export { getComposerProviderState };

type ProviderRegistryEntry = {
  getState: (input: ComposerProviderStateInput) => ComposerProviderState;
  renderTraitsMenuContent: (input: {
    threadRef?: ScopedThreadRef;
    draftId?: DraftId;
    model: string;
    models: ReadonlyArray<ServerProviderModel>;
    modelOptions: ProviderModelOptions[ProviderKind] | undefined;
    prompt: string;
    onPromptChange: (prompt: string) => void;
  }) => ReactNode;
  renderTraitsPicker: (input: {
    threadRef?: ScopedThreadRef;
    draftId?: DraftId;
    model: string;
    models: ReadonlyArray<ServerProviderModel>;
    modelOptions: ProviderModelOptions[ProviderKind] | undefined;
    prompt: string;
    onPromptChange: (prompt: string) => void;
  }) => ReactNode;
};

function hasComposerTraitsTarget(input: {
  threadRef: ScopedThreadRef | undefined;
  draftId: DraftId | undefined;
}): boolean {
  return input.threadRef !== undefined || input.draftId !== undefined;
}

const composerProviderRegistry: Record<ProviderKind, ProviderRegistryEntry> = {
  codex: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: ({
      threadRef,
      draftId,
      model,
      models,
      modelOptions,
      prompt,
      onPromptChange,
    }) =>
      !hasComposerTraitsTarget({ threadRef, draftId }) ? null : (
        <TraitsMenuContent
          provider="codex"
          models={models}
          {...(threadRef ? { threadRef } : {})}
          {...(draftId ? { draftId } : {})}
          model={model}
          modelOptions={modelOptions}
          prompt={prompt}
          onPromptChange={onPromptChange}
        />
      ),
    renderTraitsPicker: ({
      threadRef,
      draftId,
      model,
      models,
      modelOptions,
      prompt,
      onPromptChange,
    }) =>
      !hasComposerTraitsTarget({ threadRef, draftId }) ? null : (
        <ReasoningEffortPicker
          provider="codex"
          models={models}
          {...(threadRef ? { threadRef } : {})}
          {...(draftId ? { draftId } : {})}
          model={model}
          modelOptions={modelOptions}
          prompt={prompt}
          onPromptChange={onPromptChange}
        />
      ),
  },
  claudeAgent: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: ({
      threadRef,
      draftId,
      model,
      models,
      modelOptions,
      prompt,
      onPromptChange,
    }) =>
      !hasComposerTraitsTarget({ threadRef, draftId }) ? null : (
        <TraitsMenuContent
          provider="claudeAgent"
          models={models}
          {...(threadRef ? { threadRef } : {})}
          {...(draftId ? { draftId } : {})}
          model={model}
          modelOptions={modelOptions}
          prompt={prompt}
          onPromptChange={onPromptChange}
        />
      ),
    renderTraitsPicker: ({
      threadRef,
      draftId,
      model,
      models,
      modelOptions,
      prompt,
      onPromptChange,
    }) =>
      !hasComposerTraitsTarget({ threadRef, draftId }) ? null : (
        <ReasoningEffortPicker
          provider="claudeAgent"
          models={models}
          {...(threadRef ? { threadRef } : {})}
          {...(draftId ? { draftId } : {})}
          model={model}
          modelOptions={modelOptions}
          prompt={prompt}
          onPromptChange={onPromptChange}
        />
      ),
  },
  ollama: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: () => null,
    renderTraitsPicker: () => null,
  },
  cmmd: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: () => null,
    renderTraitsPicker: () => null,
  },
  apex: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: () => null,
    renderTraitsPicker: () => null,
  },
  cursor: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: () => null,
    renderTraitsPicker: () => null,
  },
  opencode: {
    getState: (input) => getComposerProviderState(input),
    renderTraitsMenuContent: () => null,
    renderTraitsPicker: () => null,
  },
};

export interface ProviderTraitsInput {
  readonly provider: ProviderKind;
  readonly threadRef?: ScopedThreadRef;
  readonly draftId?: DraftId;
  readonly model: string;
  readonly models: ReadonlyArray<ServerProviderModel>;
  readonly modelOptions: ProviderModelOptions[ProviderKind] | undefined;
  readonly prompt: string;
  readonly onPromptChange: (prompt: string) => void;
}

export function renderProviderTraitsMenuContent(input: ProviderTraitsInput): ReactNode {
  return composerProviderRegistry[input.provider].renderTraitsMenuContent({
    ...(input.threadRef ? { threadRef: input.threadRef } : {}),
    ...(input.draftId ? { draftId: input.draftId } : {}),
    model: input.model,
    models: input.models,
    modelOptions: input.modelOptions,
    prompt: input.prompt,
    onPromptChange: input.onPromptChange,
  });
}

export function renderProviderTraitsPicker(input: ProviderTraitsInput): ReactNode {
  return composerProviderRegistry[input.provider].renderTraitsPicker({
    ...(input.threadRef ? { threadRef: input.threadRef } : {}),
    ...(input.draftId ? { draftId: input.draftId } : {}),
    model: input.model,
    models: input.models,
    modelOptions: input.modelOptions,
    prompt: input.prompt,
    onPromptChange: input.onPromptChange,
  });
}

export function renderProviderTraits(input: ProviderTraitsInput) {
  return {
    menuContent: renderProviderTraitsMenuContent(input),
    picker: renderProviderTraitsPicker(input),
  };
}
